import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/modern/components/ui/carousel";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import FilePreview from "@/modern/components/file-preview";
import { useEntityFileContext } from "../context/EntityFileContext";
import { Skeleton } from "@/modern/components/ui/skeleton";
import { useState } from "react";
import ImageAnnotationEditor from "@/react/components/entityViews/files/components/editors/ImageAnnotationEditor";
import { FormattedFile } from "../context/EntityFileContext";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useFileSpotIncentive } from "@/react/components/entityViews/files/hooks/useFileSpotIncentive";
import { FileActionsMenu } from "@/react/components/entityViews/files/components/FileActionsMenu";
import { cn } from "@/modern/utils";
import { Pencil } from "lucide-react";

export const FileSliderView = () => {
  const { files, handleFileOpen, handleFileDownload, deleteFile, fetchCompanyCamPhotos, hasMorePhotos, hasMoreDocuments, isFetchingPhotos, isFetchingDocuments, entityRecord } = useEntityFileContext();
  const { project, editorHandler } = useSwivuBryntumContext();
  const showNavArrows = files.length > 3;
  const [fileToAnnotate, setFileToAnnotate] = useState<FormattedFile | null>(null);

  // Use the spot incentive hook for "Adjust Points" functionality
  const { shouldShowAdjustPoints, handleAdjustPoints } = useFileSpotIncentive({
    entityRecord,
    project,
    editorHandler
  });

  const handleNextClick = async ({ canScrollNext, scrollNext }: { canScrollNext: boolean; scrollNext: () => void }) => {
    if (!canScrollNext && (hasMorePhotos || hasMoreDocuments) && !isFetchingPhotos && !isFetchingDocuments) {
      fetchCompanyCamPhotos();
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    scrollNext();
  };

  return (
    <div className="relative">
      <Carousel
        opts={{
          align: "start",
          slidesToScroll: 5,
          watchDrag: false,
          dragFree: false,
          containScroll: "trimSnaps"
        }}
        className="w-full"
      >
        <CarouselContent className="gap-4 ml-0 overflow-visible py-2">
          {files.map((file) => (
            <CarouselItem key={file.id} className="basis-24 flex flex-col pl-0 overflow-visible">
              <div
                  className={cn(
                    "size-24 bg-muted rounded-lg overflow-hidden border transition-all duration-300 relative group cursor-pointer hover:scale-125 hover:z-10 hover:shadow-xl",
                    file.isAnnotated
                      ? "border-[#144f94]/40 hover:border-[#0096ff] hover:shadow-[#144f94]/30"
                      : "border-border hover:border-primary/50"
                  )}
                onClick={() => {
                  handleFileOpen(file);
                }}
              >
                <FilePreview isUseFileQueryParam={true} file={file} className="w-full h-full object-cover" isThumbnail={true} />

                {/* Stylish Annotated Corner Ribbon - Bottom Left */}
                {file.isAnnotated && (
                  <div className="absolute bottom-0 left-0 overflow-hidden w-16 h-16 pointer-events-none z-10">
                    <div className="absolute -left-10 bottom-0 w-24 text-center transform rotate-45 bg-gradient-to-r from-[#144f94] via-[#0096ff] to-[#144f94] text-white py-1 shadow-lg flex items-center justify-center">
                      <Pencil className="w-3 h-3 -rotate-45" />
                    </div>
                  </div>
                )}

                {/* More options */}
                <div className="z-10 absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <FileActionsMenu
                    file={file}
                    onFileOpen={handleFileOpen}
                    onFileDownload={handleFileDownload}
                    onFileAnnotate={setFileToAnnotate}
                    onFileDelete={deleteFile}
                    shouldShowAdjustPoints={shouldShowAdjustPoints}
                    onAdjustPoints={handleAdjustPoints}
                  />
                </div>
              </div>
              <Tooltip>
                <TooltipTrigger>
                  <p className="text-xs text-muted-foreground mt-2 mb-0 truncate">{file.name}</p>
                </TooltipTrigger>
                <TooltipContent>{file.name}</TooltipContent>
              </Tooltip>
            </CarouselItem>
          ))}

          {/* Show skeleton loaders when fetching more photos */}
          {isFetchingPhotos &&
            hasMorePhotos &&
            Array.from({ length: 15 }, (_, index) => index).map((box) => (
              <CarouselItem key={`skeleton-${box}`} className="basis-24 flex flex-col pl-0">
                <div className="space-y-2">
                  <Skeleton className="w-24 h-24 rounded-md shrink-0 bg-muted border" />
                  <Skeleton className="w-12 h-4 rounded-md bg-muted border" />
                </div>
              </CarouselItem>
            ))}
        </CarouselContent>

        {/* Navigation arrows - positioned within content area */}
        {showNavArrows && (
          <>
            <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 bg-background/90 hover:bg-background border shadow-sm z-10" />
            <CarouselNext
              onSwivuCarouselClick={handleNextClick}
              disabled={isFetchingPhotos}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 bg-background/90 hover:bg-background border shadow-sm z-10"
            />
          </>
        )}
      </Carousel>

      {/* Image Annotation Editor */}
      {fileToAnnotate && fileToAnnotate.originalImageUrl && (
        <ImageAnnotationEditor project={project} entityRecord={entityRecord} fileToAnnotate={fileToAnnotate} onClose={() => setFileToAnnotate(null)} />
      )}
    </div>
  );
};
